/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-unused-vars */
import './CreateServiceRequestForm.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import anchorme from 'anchorme';
import { message } from 'antd';
import { ContentState, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';

import Header from '../../../../components/Header/Header';
import { uploadFilesInServiceRequest } from '../../../../context/thunks/tqConnectServiceRequestThunk';
import { resetServiceRequestModalState } from '../../../../context/tqConnectServiceRequestReducer';
import {
  useCreateTQServiceRequest,
  useUpdateTQServiceRequest,
} from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  EUploadModuleType,
  IFileAttachment,
} from '../../../../types/fileTypes';
import { ROUTES } from '../../../../types/routes';
import {
  ITQCreateServiceRequestBody,
  TQServiceRequestStatusType,
} from '../../../../types/tqConnectTypes/service-requests';
import AttachedFileButton from '../AttachedFileButton/AttachedFileButton';
import AddTQConnectTags from '../SelectTQConnectTags/AddTQConnectTags';
import type { ITQTagSelectOption } from '../SelectTQConnectTags/types';
import CreateServiceRequestSuccessView from './CreateServiceRequestSuccessView';
import ServiceRequestDescriptionEditor from './ServiceRequestDescriptionEditor';
import ServiceRequestTitleEditor from './ServiceRequestTitleEditor';

const TITLE_PLACEHOLDER = 'E.g. Need help reviewing my pitch deck';
const DESCRIPTION_PLACEHOLDER =
  'Describe what you need support with, any goals or context.';

type FormStep = 'form' | 'title' | 'description';

export interface CreateServiceRequestFormValues {
  title: string;
  description: string;
  tagIds: string[];
  tags: ITQTagSelectOption[];
  files: File[];
}

export interface AttachedFileDisplay {
  file: File;
  id: string;
  name: string;
  size: number;
  typeLabel: string;
}

const block = 'create-service-request';

const CreateServiceRequestForm: React.FC = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const { serviceRequestModalState } = useAppSelector(
    (state) => state.tqConnectServiceRequest,
  );

  const isEditing = useMemo(
    () => Boolean(serviceRequestModalState?.serviceRequestId),
    [serviceRequestModalState?.serviceRequestId],
  );

  const isDuplicating = useMemo(
    () =>
      Boolean(
        serviceRequestModalState?.serviceRequest?.title &&
        serviceRequestModalState?.serviceRequest?.description &&
        serviceRequestModalState?.serviceRequest?.tags?.length,
      ),
    [
      serviceRequestModalState?.serviceRequest?.title,
      serviceRequestModalState?.serviceRequest?.description,
      serviceRequestModalState?.serviceRequest?.tags?.length,
    ],
  );

  const [activeStep, setActiveStep] = useState<FormStep>('form');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionEditorState, setDescriptionEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [tags, setTags] = useState<ITQTagSelectOption[]>([]);
  const [attachments, setAttachments] = useState<IFileAttachment[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFileDisplay[]>([]);
  const [status, setStatus] = useState<TQServiceRequestStatusType>('open');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { updateServiceRequest, isPending: isUpdatingServiceRequest } =
    useUpdateTQServiceRequest(serviceRequestModalState?.serviceRequestId || '');

  const { createServiceRequest, isPending: isCreatingServiceRequest } =
    useCreateTQServiceRequest();

  const reset = useCallback(() => {
    dispatch(resetServiceRequestModalState());
    setAttachedFiles([]);
    setAttachments([]);
    setTags([]);
    setDescriptionEditorState(EditorState.createEmpty());
    setTitle('');
    setActiveStep('form');
    setShowSuccess(false);
    setIsSubmitting(false);
  }, [dispatch]);

  const descriptionPlainText = descriptionEditorState
    .getCurrentContent()
    .getPlainText();

  const isFormValid = useMemo(
    () =>
      title.trim().length > 0 &&
      descriptionPlainText.trim().length > 0 &&
      !isSubmitting,
    [title, descriptionPlainText, isSubmitting],
  );

  const handleClose = useCallback(() => {
    navigate.goBack();
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);
    try {
      let uploadedFiles: IFileAttachment[] = [];

      if (attachedFiles.length > 0) {
        const resp = await dispatch(
          uploadFilesInServiceRequest(
            attachedFiles.map((file) => file.file),
            setIsUploading,
            (event) => {
              if (typeof event.progress === 'number') {
                setUploadProgress(event.progress);
              }
            },
            EUploadModuleType.TQ_CONNECT_SERVICE_REQUEST,
          ),
        );

        if (resp && resp?.length > 0) {
          uploadedFiles = resp;
        }
      }

      const reqBody: ITQCreateServiceRequestBody = {
        title: title,
        description: description,
        tags: tags,
        visibility: serviceRequestModalState?.requestedTo?._id
          ? 'private'
          : 'public',
        status: status,
        requestedTo: serviceRequestModalState?.requestedTo?._id,
        attachments: [...(attachments || []), ...uploadedFiles],
      };

      if (isEditing && serviceRequestModalState?.serviceRequestId) {
        updateServiceRequest(
          {
            serviceRequestId: serviceRequestModalState?.serviceRequestId,
            data: reqBody,
          },
          {
            onSuccess: () => {
              message.success(`${title.toTitleCase()} updated`);
              navigate.goBack();
            },
            onError: () => {
              setUploadProgress(0);
              setIsUploading(false);
            },
          },
        );

        return;
      }

      createServiceRequest(reqBody, {
        onSuccess: (resp) => {
          message.success(
            serviceRequestModalState?.requestedTo?._id
              ? `Request sent to ${serviceRequestModalState?.requestedTo?.name}`
              : `Service Request created`,
          );
          setShowSuccess(true);
        },
        onError: () => {
          setUploadProgress(0);
          setIsUploading(false);
        },
      });
    } catch (error) {
      showAppError(error);
    } finally {
      setIsUploading(false);
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  }, [
    isFormValid,
    attachedFiles,
    title,
    description,
    tags,
    serviceRequestModalState?.requestedTo?._id,
    serviceRequestModalState?.requestedTo?.name,
    serviceRequestModalState?.serviceRequestId,
    status,
    attachments,
    isEditing,
    createServiceRequest,
    dispatch,
    updateServiceRequest,
    navigate,
  ]);

  const goToMyRequests = useCallback(() => {
    navigate(
      ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY,
      {},
      { reset: true },
    );
  }, [navigate]);

  const viewAllRequests = useCallback(() => {
    if (serviceRequestModalState?.requestedTo?._id) {
      navigate(
        ROUTES.USER_PROFILE,
        {
          userId: serviceRequestModalState?.requestedTo?._id,
        },
        { reset: true },
      );
    } else {
      navigate(
        ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER,
        {},
        { reset: true },
      );
    }
  }, [navigate, serviceRequestModalState?.requestedTo?._id]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    if (serviceRequestModalState?.serviceRequest?.title) {
      setTitle(serviceRequestModalState?.serviceRequest?.title || '');
      setDescription(
        serviceRequestModalState?.serviceRequest?.description || '',
      );
      const blocksFromHtml = htmlToDraft(
        serviceRequestModalState?.serviceRequest?.description || '',
      );
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap,
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setDescriptionEditorState(newEditorState);
      setTags(
        serviceRequestModalState?.serviceRequest?.tags?.map((tag) => ({
          key: tag._id,
          value: tag.name,
          label: tag.name,
          _id: tag._id,
          name: tag.name,
          createdBy: tag.createdBy,
          usage: tag.usage,
        })) || [],
      );
      setAttachments(
        serviceRequestModalState?.serviceRequest?.attachments || [],
      );
    }
  }, [serviceRequestModalState?.serviceRequest]);

  const successContent = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let title = (
      <h1 className={`${block}__success-title`}>Service Request Created</h1>
    );
    let description = (
      <p className={`${block}__success-description`}>
        Your service request has been created. You&apos;ll find it listed under
        &quot;My Requests&quot; to manage or update.
      </p>
    );
    if (serviceRequestModalState?.requestedTo?._id) {
      title = (
        <h1 className={`${block}__success-title`}>
          Request Sent to <b>{serviceRequestModalState?.requestedTo?.name}</b>
        </h1>
      );
      description = (
        <p className={`${block}__success-description`}>
          Your service request has been sent. You’ll be notified once{' '}
          <b>{serviceRequestModalState?.requestedTo?.name}</b> responds.
        </p>
      );
    }

    return {
      title,
      description,
    };
  }, [
    serviceRequestModalState?.requestedTo?._id,
    serviceRequestModalState?.requestedTo?.name,
  ]);

  const handleTitleDone = useCallback((newTitle: string) => {
    setTitle(newTitle);
    setActiveStep('form');
  }, []);

  const handleDescriptionDone = useCallback((html: string) => {
    setDescription(anchorme(html));
    const blocksFromHtml = htmlToDraft(html);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap,
    );
    setDescriptionEditorState(EditorState.createWithContent(contentState));
    setActiveStep('form');
  }, []);

  if (showSuccess) {
    return (
      <CreateServiceRequestSuccessView
        title={successContent.title}
        description={successContent.description}
        isPrivateRequest={Boolean(serviceRequestModalState?.requestedTo?._id)}
        onGoToMyRequests={goToMyRequests}
        onViewAllOrProfile={viewAllRequests}
      />
    );
  }

  if (activeStep === 'title') {
    return (
      <ServiceRequestTitleEditor
        initialTitle={title}
        onDone={handleTitleDone}
        onClose={() => setActiveStep('form')}
      />
    );
  }

  if (activeStep === 'description') {
    return (
      <ServiceRequestDescriptionEditor
        initialDescriptionHtml={description}
        onDone={handleDescriptionDone}
        onClose={() => setActiveStep('form')}
      />
    );
  }

  // Main form: tappable title and description rows
  return (
    <div className={`${block} page__container`}>
      <Header
        title="Create Service Request"
        handleBack={handleClose}
        backType="cross"
        actionItems={[
          <button
            key="submit"
            type="button"
            className={`${block}__header-submit ${
              isFormValid ? `${block}__header-submit--active` : ''
            }`}
            disabled={!isFormValid || isSubmitting}
            onClick={handleSubmit}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>,
        ]}
      />
      <div
        className={`${block}__scroll page__content`}
        id="create-service-request-scroll">
        <div className={`${block}__form`}>
          <label className={`${block}__label`}>Title</label>
          <div
            className={`${block}__field ${block}__field--tappable`}
            role="button"
            tabIndex={0}
            onClick={() => setActiveStep('title')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveStep('title');
              }
            }}>
            <span
              className={`${block}__tappable-value ${
                !title.trim() ? `${block}__tappable-value--placeholder` : ''
              }`}>
              {title.trim() || TITLE_PLACEHOLDER}
            </span>
          </div>

          <label className={`${block}__label`}>Description</label>
          <div
            className={`${block}__field ${block}__field--tappable`}
            role="button"
            tabIndex={0}
            onClick={() => setActiveStep('description')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveStep('description');
              }
            }}>
            <span
              className={`${block}__tappable-value ${block}__tappable-value--multiline ${
                !descriptionPlainText.trim()
                  ? `${block}__tappable-value--placeholder`
                  : ''
              }`}>
              {descriptionPlainText.trim()
                ? `${descriptionPlainText.trim().slice(0, 80)}${descriptionPlainText.length > 80 ? '...' : ''}`
                : DESCRIPTION_PLACEHOLDER}
            </span>
          </div>

          <label className={`${block}__label`}>Add tags</label>
          <div className={`${block}__field ${block}__field--tags`}>
            <AddTQConnectTags
              value={tags}
              onChange={(value) => setTags(value || [])}
              placeholder="Select"
              className={`${block}__tags-select`}
            />
          </div>
          <AttachedFileButton
            attachedFiles={attachedFiles}
            setAttachedFiles={setAttachedFiles}
          />

          <div className={`${block}__keyboard-spacer`} />
        </div>
      </div>
    </div>
  );
};

export default CreateServiceRequestForm;
